import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { useModelCatalogStore } from "@/lib/stores/model-catalog-store";
import { useTimingStatsStore } from "@/lib/stores/timing-stats-store";
import { useQuery } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { ProjectNavbar } from "@/components/project/ProjectNavbar";
import { ChatPanel } from "@/components/project/ChatPanel";
import { CanvasPanel } from "@/components/project/CanvasPanel";
import { CanvasPanelBoundary } from "@/components/project/CanvasPanelBoundary";
import { SettingsPanel } from "@/components/project/SettingsPanel";
import { createProjectsApi } from "@/lib/api/projects";
import { createRunsApi } from "@/lib/api/runs";
import { useExecutionStore } from "@/lib/stores/execution-store";
import type { ChatAttachmentImageRef } from "@/lib/api/chat";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CompositorEditor } from "@/components/project/compositor/CompositorEditor";

export function ProjectPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();
  const [leftPanelWidth, setLeftPanelWidth] = useState(25);
  const navState = location.state as
    | { initialAttachmentImage?: ChatAttachmentImageRef | null; initialMode?: string }
    | null;
  const initialAttachmentImage = navState?.initialAttachmentImage ?? null;
  const initialMode = navState?.initialMode ?? null;

  const fetchModelCatalog = useModelCatalogStore((s) => s.fetch);
  const seedLatestOutputs = useExecutionStore((s) => s.seedLatestOutputs);
  const fetchTimingStats = useTimingStatsStore((s) => s.fetch);

  useEffect(() => {
    fetchModelCatalog(getToken);
    fetchTimingStats(getToken);
  }, [fetchModelCatalog, fetchTimingStats, getToken]);

  useEffect(() => {
    if (!projectId) return;
    const runsApi = createRunsApi({ getToken });
    runsApi.latestNodeOutputs(projectId)
      .then((data) => seedLatestOutputs(data))
      .catch(() => {});
  }, [projectId, getToken, seedLatestOutputs]);

  const projectsApi = createProjectsApi({ getToken });
  const {
    data: project,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => projectsApi.get(projectId!),
    enabled: !!projectId,
  });

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center gap-4 bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-semibold">Project not found</h1>
          <p className="text-muted-foreground mt-2">
            {error instanceof Error
              ? error.message
              : "The project you're looking for doesn't exist or you don't have access to it."}
          </p>
        </div>
        <Button onClick={() => navigate("/dashboard/projects")}>
          Back to projects
        </Button>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex h-screen w-screen flex-col bg-background">
        <ProjectNavbar
          leftPanelWidth={leftPanelWidth}
          projectId={project.id}
          projectName={project.name}
          isStarred={project.isStarred}
          getToken={getToken}
        />

        <ResizablePanelGroup orientation="horizontal" className="w-full h-full">
          {/* Chat Panel */}
          <ResizablePanel
            defaultSize="25%"
            minSize="20%"
            maxSize="30%"
            onResize={(size) => setLeftPanelWidth(size.asPercentage)}
            className="bg-background"
          >
            <ChatPanel
              project={project}
              initialAttachmentImage={initialAttachmentImage}
              initialMode={initialMode}
            />
          </ResizablePanel>

          <ResizableHandle className="bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 data-[resize-handle-active]:bg-border" />

          {/* Canvas Panel */}
          <ResizablePanel
            defaultSize="55%"
            minSize="30%"
            className="bg-background"
          >
            <CanvasPanelBoundary>
              <CanvasPanel project={project} />
            </CanvasPanelBoundary>
          </ResizablePanel>

          <ResizableHandle className="bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 data-[resize-handle-active]:bg-border" />

          {/* Settings Panel */}
          <ResizablePanel
            defaultSize="20%"
            minSize="20%"
            maxSize="30%"
            className="bg-background"
          >
            <SettingsPanel />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* Compositor editor dialog — renders globally, only visible when isOpen */}
      <CompositorEditor />
    </TooltipProvider>
  );
}
